import type {
  HotspotObjectExtension,
  InteractiveViewerExtension,
  LayoutOverlayExtension,
  ViewerSlotExtension,
  ViewerSlotName,
} from './contracts/extensions';

export interface ViewerExtensionRegistry {
  extensions: readonly InteractiveViewerExtension[];
  hotspotObjects: ReadonlyMap<string, HotspotObjectExtension>;
  layoutOverlays: ReadonlyMap<string, LayoutOverlayExtension>;
  slots: ReadonlyMap<ViewerSlotName, readonly ViewerSlotExtension[]>;
}

const assertUnique = <T>(
  map: Map<string, T>,
  key: string,
  value: T,
  contributionType: string,
): void => {
  if (map.has(key)) {
    throw new Error(`Duplicate ${contributionType} contribution: "${key}"`);
  }
  map.set(key, value);
};

export const createViewerExtensionRegistry = (
  extensions: readonly InteractiveViewerExtension[] = [],
): ViewerExtensionRegistry => {
  const extensionIds = new Map<string, InteractiveViewerExtension>();
  const hotspotObjects = new Map<string, HotspotObjectExtension>();
  const layoutOverlays = new Map<string, LayoutOverlayExtension>();
  const mutableSlots = new Map<ViewerSlotName, ViewerSlotExtension[]>();

  for (const extension of extensions) {
    assertUnique(extensionIds, extension.id, extension, 'viewer extension');

    for (const hotspotObject of extension.hotspotObjects ?? []) {
      assertUnique(
        hotspotObjects,
        hotspotObject.type,
        hotspotObject,
        'hotspot object',
      );
    }

    for (const overlay of extension.layoutOverlays ?? []) {
      assertUnique(layoutOverlays, overlay.id, overlay, 'layout overlay');
    }

    for (const slot of extension.slots ?? []) {
      const contributions = mutableSlots.get(slot.slot) ?? [];
      if (contributions.some((contribution) => contribution.id === slot.id)) {
        throw new Error(
          `Duplicate slot contribution: "${slot.slot}:${slot.id}"`,
        );
      }
      contributions.push(slot);
      mutableSlots.set(slot.slot, contributions);
    }
  }

  const slots = new Map<ViewerSlotName, readonly ViewerSlotExtension[]>();
  for (const [slot, contributions] of mutableSlots) {
    slots.set(slot, Object.freeze([...contributions]));
  }

  return {
    extensions: Object.freeze([...extensions]),
    hotspotObjects,
    layoutOverlays,
    slots,
  };
};
